import { useEffect, useState } from 'react'
import { Image } from 'src/components/ui/Image'
import { Link } from '@faststore/ui'
import DktLoader from 'src/components/DktLoader/DktLoader'
import classNames from 'classnames'
import { InView } from 'react-intersection-observer'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { getProductQueryObject } from 'src/utils/amplitude/useProductObject'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { useCheckout } from 'src/sdk/checkout/useCheckout'
import { formatterPrice } from 'src/utils/formatterPrice'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { ENABLE_GUEST_CHECKOUT_EXPERIMENT_ID } from 'src/components/common/StatsigExposure/StatsigExposure'
import { Statsig } from 'src/sdk/statsig'

import { SkuSelector } from '../../sections/Sidebar/SkuSelector'
import type { Product } from '../../types/product'
import type { Image as ImageType, ProductItem } from '../../types/productItem'
import { getInitialItem } from '../../../utils/get-initial-sku'
import type { AddToCartFn } from '../hooks/useAddToCartLogic'

export interface ModalProductToAddProps {
  product: Product
  currentProduct: ProductItem
  Images: ImageType[]
  addToCartFn: AddToCartFn
  isLoading?: boolean
  style?: 'add-item' | 'change-item'
  modalName?: string
}

export function ModalProductToAdd({
  product,
  currentProduct,
  Images,
  addToCartFn,
  isLoading,
  style = 'add-item',
  modalName = 'compra pela vitrine/plp',
}: ModalProductToAddProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [variations, setVariations] = useState<any[]>([])
  const [isShouldDisable, setIsShouldDisable] = useState(false)
  const [currentSkuState, setCurrentSkuState] = useState(currentProduct)
  const isGuestCheckoutEnabled = Statsig.useStatsigFeatureGateValue(
    ENABLE_GUEST_CHECKOUT_EXPERIMENT_ID
  )

  const { pageData } = useGlobalContext()
  const initialSku =
    currentProduct ?? getInitialItem(product.items, product.linkText)

  const standardObject = getStandardObject(pageData)
  const productObject = getProductQueryObject(currentProduct)

  const { isAvailable } = currentProduct
  const {
    orderForm: { items, messages },
    isLoading: checkoutLoading,
  } = useCheckout()

  const loading = isLoading ?? checkoutLoading

  const {
    sellerDefault: {
      sellerOffer: {
        installments,
        bestInstallment: bestInstallmentDefault,
        cashPrice,
        cashDiscountPercentage,
      },
    },
    offer: { discountPercentage },
  } = currentProduct ?? {}

  const notDecathlonCardInstallments = installments.filter(
    ({ onlyDecathlonCard }) => !onlyDecathlonCard
  )

  const bestInstallment = bestInstallmentDefault?.onlyDecathlonCard
    ? notDecathlonCardInstallments.pop()
    : bestInstallmentDefault

  const hasInstallmentDifference =
    !!cashDiscountPercentage || (bestInstallment?.NumberOfInstallments ?? 0) > 1

  const { linkText, productName } = product

  const buttonDisabledClasses =
    'cursor-not-allowed border-restructure-border-disabled bg-restructure-background-disabled text-restructure-disabled hover:border-restructure-border-disabled hover:text-restructure-disabled'

  const availableQtty = currentSkuState.sellerDefault?.sellerOffer?.quantity
  const qttyAlreadyInCart = items?.find(
    (item) => item.id === currentSkuState.itemId
  )?.quantity

  const quantityNotAvailableMessage = messages?.find(
    (message) => message.code === 'itemQuantityNotAvailable'
  )

  const cannotIncreaseQuantity =
    quantityNotAvailableMessage?.fields?.ean === currentSkuState.ean

  const sendModalViewEvent = () => {
    dispatchAmplitudeEvent({
      eventName: 'Modal Viewed',
      eventData: {
        ...standardObject,
        ...productObject,
        'modal nome': modalName,
      },
    })
  }

  useEffect(() => {
    const shouldDisable =
      isAvailable === false ||
      (qttyAlreadyInCart ?? 0) >= (availableQtty ?? 0) ||
      !!cannotIncreaseQuantity

    setIsShouldDisable(!!shouldDisable)
  }, [availableQtty, cannotIncreaseQuantity, isAvailable, qttyAlreadyInCart])

  return (
    <InView
      as="div"
      onChange={(inView) => {
        if (!inView) {
          return null
        }

        return sendModalViewEvent()
      }}
      triggerOnce
      className="px-4 restructure-tablet:px-8"
    >
      <Link
        className="py-4 flex gap-3 restructure-tablet:py-8 hover:no-underline"
        href={`/${linkText}/p`}
      >
        <Image
          src={Images[0].imageUrl}
          alt={product?.productName ?? 'Imagem do carrossel'}
          title={product?.productName ?? Images[0]?.imageText ?? ''}
          width={120}
          height={120}
          loading="lazy"
          sizes="120px"
          className=" object-contain mix-blend-multiply shrink-0"
        />
        <div>
          <p className="text-body-sm-link text-restructure-primary mb-4 line-clamp-2">
            {productName}
          </p>
          {discountPercentage > 0 && (
            <p className="text-font-size-1 text-tertiary line-through mb-1.5">
              {formatterPrice(currentProduct?.offer?.listPrice)}
            </p>
          )}
          <p className="flex gap-1 items-end mb-1.5 text-font-size-3 text-restructure-primary  font-medium leading-[100%]">
            {formatterPrice(
              cashDiscountPercentage ? cashPrice : currentProduct?.offer?.price
            )}{' '}
            {!!cashDiscountPercentage && (
              <span className="text-font-size-1 font-normal text-secondary">
                à vista
              </span>
            )}
            {discountPercentage > 0 && (
              <span className="w-fit inline-block bg-redDiscount text-font-size-1 leading-[16px] font-medium text-[#ffffff] px-1">
                {discountPercentage}% OFF
              </span>
            )}
          </p>
          {installments.length > 0 &&
            bestInstallment &&
            hasInstallmentDifference && (
              <div className="text-font-size-1 font-medium text-secondary">
                ou{' '}
                <span className="font-semibold">
                  {bestInstallment.NumberOfInstallments}x de{' '}
                  {formatterPrice(bestInstallment.Value)}
                </span>
                {!bestInstallment.InterestRate && ' sem juros'}
              </div>
            )}
        </div>
      </Link>
      <SkuSelector
        classContainer="gap-6 restructure-tablet:pt-0 restructure-tablet:gap-10"
        productFromProps={product}
        setVariationsFromProps={setVariations}
        variationsFromProps={variations}
        currentSkuFromProps={currentSkuState}
        setCurrentSkuFromProps={setCurrentSkuState}
        initialSkuFromProps={initialSku}
      />
      <div className="flex flex-col gap-2 mt-8 restructure-tablet:gap-4 restructure-tablet:mt-12">
        {style === 'add-item' ? (
          <>
            {isGuestCheckoutEnabled && (
              <button
                className={classNames(
                  'flex-1 w-full px-xxs py-5 min-h-12 rounded-round font-semibold text-base bg-restructure-background-action-1 hover:bg-restructure-background-action-1-hover text-restructure-primary-inverted restructure-tablet:min-h-16',
                  {
                    [buttonDisabledClasses]: isShouldDisable,
                  }
                )}
                onClick={(e) =>
                  addToCartFn('buyNow', currentSkuState ?? initialSku, e)
                }
                disabled={isShouldDisable}
              >
                {loading ? (
                  <div className="flex items-center justify-center w-full">
                    <DktLoader width={20} variant="black" />
                  </div>
                ) : (
                  <span>Comprar agora</span>
                )}
              </button>
            )}
            <button
              data-testid="modal-add-to-cart"
              className={classNames(
                'flex-1 w-full px-xxs py-5 min-h-12 rounded-round font-semibold text-base bg-restructure-background-neutral-3 text-primaryBlue restructure-tablet:min-h-16',
                {
                  [buttonDisabledClasses]: isShouldDisable,
                }
              )}
              onClick={(e) =>
                addToCartFn('addToCart', currentSkuState ?? initialSku, e)
              }
              disabled={isShouldDisable}
            >
              {loading ? (
                <div className="flex items-center justify-center w-full">
                  <DktLoader width={20} variant="white" />
                </div>
              ) : (
                <span>Adicionar ao carrinho</span>
              )}
            </button>
          </>
        ) : (
          <button
            data-testid="cart-item-change-sku-save"
            className={classNames(
              'flex-1 w-full  min-h-12 rounded-round font-semibold text-base bg-restructure-background-neutral-12 text-white',
              {
                [buttonDisabledClasses]: isShouldDisable,
              }
            )}
            onClick={(e) =>
              addToCartFn('addToCart', currentSkuState ?? initialSku, e)
            }
            disabled={isShouldDisable}
          >
            {loading ? (
              <div className="flex items-center justify-center w-full">
                <DktLoader width={20} variant="white" />
              </div>
            ) : (
              <span>Salvar</span>
            )}
          </button>
        )}

        <Link
          onClick={() => {
            dispatchAmplitudeEvent({
              eventName: 'Click Link View Product Details',
              eventData: {
                ...standardObject,
                ...productObject,
                'modal nome': 'compra pela vitrine/plp',
              },
            })
          }}
          href={`/${linkText}/p`}
          className="w-full py-2 cursor-pointer"
        >
          <span className="w-full flex justify-center text-font-size-3 leading-6 font-medium text-tertiary underline text-center">
            Ver detalhes do produto
          </span>
        </Link>
      </div>
    </InView>
  )
}
